import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { Plus, CheckCircle, XCircle, Clock, Eye, Trash } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { DocumentUploadSection } from '../components/DocumentUploadSection';

export default function PPAPPage() {
  const [submissions, setSubmissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [subRes, projRes] = await Promise.all([
        api.get('/ppap/submissions'),
        api.get('/apqp/projects')
      ]);
      setSubmissions(subRes.data);
      setProjects(projRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    try {
      await api.post('/ppap/submissions', {
        project_id: formData.get('project_id'),
        level: parseInt(formData.get('level')),
        comments: formData.get('comments'),
      });
      toast.success('PPAP submission created');
      setShowCreateModal(false);
      loadData();
    } catch (error) {
      toast.error('Failed to create submission');
    }
  };

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/ppap/submissions/${id}/status?status=${status}`);
      toast.success(`Submission ${status}`);
      loadData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDeleteSubmission = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this PPAP submission? This action cannot be undone.')) {
      return;
    }
    try {
      await api.delete(`/ppap/submissions/${submissionId}`);
      toast.success('Submission deleted successfully');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete submission');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved': return <CheckCircle size={20} weight="fill" className="text-green-600" />;
      case 'rejected': return <XCircle size={20} weight="fill" className="text-red-600" />;
      case 'submitted': return <Clock size={20} weight="fill" className="text-blue-600" />;
      default: return <Clock size={20} className="text-gray-400" />;
    }
  };

  return (
    <div data-testid="ppap-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">PPAP Management</h1>
          <p className="mt-1 text-sm text-gray-600">Production Part Approval Process submissions</p>
        </div>
        <button
          data-testid="create-ppap-button"
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-gray-900 text-white px-4 py-2 rounded-sm hover:bg-gray-800 transition-colors"
        >
          <Plus size={20} weight="bold" />
          New Submission
        </button>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading submissions...</div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200 rounded-sm">
          <p className="text-gray-500">No PPAP submissions yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Submission ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Level</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Submitted</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Actions</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-900 uppercase tracking-wider">Documents</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {submissions.map((sub, idx) => (
                <tr key={sub.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'} data-testid={`ppap-row-${idx}`}>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900 mono">{sub.id.slice(0, 8)}</td>
                  <td className="px-6 py-4 text-sm text-gray-600 mono">Level {sub.level}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(sub.status)}
                      <span className={`px-2 py-1 text-xs font-medium rounded-sm ${
                        sub.status === 'approved' ? 'bg-green-100 text-green-700' :
                        sub.status === 'rejected' ? 'bg-red-100 text-red-700' :
                        sub.status === 'submitted' ? 'bg-blue-100 text-blue-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {sub.status.toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">{new Date(sub.submission_date).toLocaleDateString()}</td>
                  <td className="px-6 py-4">
                    {sub.status === 'submitted' && (user?.role === 'admin' || user?.role === 'qa_manager') && (
                      <div className="flex gap-2">
                        <button
                          data-testid={`approve-ppap-${idx}`}
                          onClick={() => updateStatus(sub.id, 'approved')}
                          className="px-3 py-1 bg-green-600 text-white text-xs font-medium rounded-sm hover:bg-green-700"
                        >
                          Approve
                        </button>
                        <button
                          data-testid={`reject-ppap-${idx}`}
                          onClick={() => updateStatus(sub.id, 'rejected')}
                          className="px-3 py-1 bg-red-600 text-white text-xs font-medium rounded-sm hover:bg-red-700"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                    {sub.status === 'draft' && (
                      <button
                        onClick={() => updateStatus(sub.id, 'submitted')}
                        className="px-3 py-1 bg-blue-600 text-white text-xs font-medium rounded-sm hover:bg-blue-700"
                      >
                        Submit
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`view-ppap-docs-${idx}`}
                        onClick={() => setSelectedSubmission(sub)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-700 text-sm font-medium"
                      >
                        <Eye size={16} /> View
                      </button>
                      {(user?.role === 'admin' || user?.role === 'qa_manager') && (
                        <button
                          data-testid={`delete-ppap-${idx}`}
                          onClick={() => handleDeleteSubmission(sub.id)}
                          className="flex items-center gap-1 text-red-600 hover:text-red-700 text-sm font-medium"
                        >
                          <Trash size={16} /> Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-sm w-full max-w-md">
            <div className="border-b border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900">Create PPAP Submission</h2>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PROJECT</label>
                <select
                  name="project_id"
                  data-testid="ppap-project-select"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="">Select project...</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>{p.project_name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">PPAP LEVEL</label>
                <select
                  name="level"
                  data-testid="ppap-level-select"
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                >
                  <option value="1">Level 1</option>
                  <option value="2">Level 2</option>
                  <option value="3">Level 3</option>
                  <option value="4">Level 4</option>
                  <option value="5">Level 5</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-2">COMMENTS</label>
                <textarea
                  name="comments"
                  data-testid="ppap-comments-input"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="ppap-create-submit"
                  className="flex-1 px-4 py-2 bg-gray-900 text-white rounded-sm hover:bg-gray-800 transition-colors"
                >
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedSubmission && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-sm w-full max-w-3xl my-8">
            <div className="border-b border-gray-200 p-6 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">PPAP Submission Documents</h2>
                <p className="text-sm text-gray-600 mt-1">
                  Level {selectedSubmission.level} • Status: {selectedSubmission.status.toUpperCase()}
                </p>
              </div>
              <button
                onClick={() => setSelectedSubmission(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="p-6">
              <DocumentUploadSection 
                category="ppap"
                relatedId={selectedSubmission.id}
                title="PPAP Evidence Documents"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
